import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";
import { useModal } from "@/hooks/useModal";
import { useState } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const { openDemo } = useModal();
  const [status, setStatus] = useState<FormStatus>("idle");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const formData = new FormData(e.currentTarget);
      const res = await fetch("https://readdy.ai/api/form/d7poja4dj3knvn2p1u00", {
        method: "POST",
        body: new URLSearchParams(formData as never),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.ok) {
        setStatus("success");
        setName("");
        setEmail("");
        setInterest("");
        setMessage("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <PageShell>
      <section className="w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Contact
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
                Get in Touch
              </h1>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                Whether you need a quote, a demo, or just want to say hello - we are here.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 max-w-5xl mx-auto">
            <ScrollReveal>
              <div className="space-y-6">
                <div className="mb-1">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Presuni Ltd
                  </h2>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    Company Reg: 05745823
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-brand-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        Address
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        10 Winrush Close, Dudley, DY3 2NE, United Kingdom
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-brand-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        Email
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        info@presuni.co.uk
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-brand-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        Phone
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        +44 (0) 7788 1322 63
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-dark-border">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Want to see AHIS in action? Book a live demo with our team.
                  </p>
                  <button
                    onClick={openDemo}
                    className="px-6 py-2.5 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Book Demo
                  </button>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Typical response time: within 24 hours during business days.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              {status === "success" ? (
                <div className="p-8 rounded-2xl bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/20 text-center">
                  <div className="w-14 h-14 rounded-full bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-brand-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Message Sent
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    We will get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border space-y-4"
                >
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                      Name
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                      Interest
                    </label>
                    <select
                      name="interest"
                      required
                      value={interest}
                      onChange={(e) => setInterest(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all appearance-none"
                    >
                      <option value="">Select interest</option>
                      <option value="ahis">AHIS SaaS</option>
                      <option value="cybersecurity">Cybersecurity</option>
                      <option value="software">Software Development</option>
                      <option value="consulting">IT Consulting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all resize-none"
                      placeholder="How can we help?"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {message.length}/500
                    </p>
                  </div>

                  {status === "error" && (
                    <p className="text-sm text-red-500">
                      Something went wrong. Please try again.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full px-5 py-3 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {status === "submitting" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageShell>
  );
}